import React from 'react';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { IPostDirect } from '../models/post';
import PostPreview from './post-preview';
import { Button } from './button';
import { CgSpinnerAlt } from 'react-icons/cg';

const NEWS_QUERY = gql`
	query GetPosts($first: Int!, $after: String) {
		posts(first: $first, after: $after, where: { categoryId: 1 }) {
			pageInfo {
				hasNextPage
				endCursor
			}
			edges {
				cursor
				node {
					id
					title
					slug
					date
					excerpt
					featuredImage {
						node {
							sourceUrl
						}
					}
					tags(first: 1) {
						nodes {
							name
						}
					}
				}
			}
		}
	}
`;

interface PageInfo {
	hasNextPage: boolean;
	endCursor: string;
}
interface Edge {
	cursor: string;
	node: IPostDirect;
}

interface IProps {
	posts: {
		pageInfo?: PageInfo;
		edges: Edge[];
	};
}

export default function LoadMorePosts() {
	const { data, loading, fetchMore } = useQuery<IProps>(NEWS_QUERY, {
		variables: {
			first: 9,
			after: null,
		},
		notifyOnNetworkStatusChange: true,
	});

	const fetchMorePosts = () => {
		const lastCursor = data?.posts?.edges[data.posts.edges.length - 1].cursor ?? null;

		fetchMore({
			variables: {
				first: 6,
				after: lastCursor,
			},
			updateQuery: (prevData, { fetchMoreResult }) => {
				if (!fetchMoreResult) return prevData;
				return {
					posts: {
						edges: [...prevData.posts?.edges, ...fetchMoreResult.posts.edges],
						pageInfo: fetchMoreResult.posts.pageInfo,
						__typename: 'PostsConnection',
					},
				};
			},
		});
	};

	const filteredPosts = data?.posts?.edges?.length && data.posts.edges.length >= 3 ? data.posts.edges.slice(3) : [];

	const morePostAvailable = Boolean(data?.posts?.pageInfo?.hasNextPage);
	const svgSpin = <CgSpinnerAlt className="animate-spin" />;

	if (!data && loading) {
		return (
			<div className="flex flex-col items-center py-6">
				{svgSpin}
				<p>Meer nieuws laden...</p>
			</div>
		);
	}

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				{filteredPosts.map(({ node }) => (
					<PostPreview
						key={node.slug}
						title={node.title}
						coverImage={node.featuredImage}
						date={node.date}
						slug={node.slug}
						excerpt={node.excerpt}
						bgColor="solid"
					/>
				))}
			</div>
			{morePostAvailable && (
				<div className="my-8 flex justify-center">
					<Button primary onClick={fetchMorePosts} className="w-48 flex justify-center items-center">
						{loading ? svgSpin : 'Meer nieuws'}
					</Button>
				</div>
			)}
		</>
	);
}
